import { useEffect, useState } from "react";
import { useParams, Link, useLocation, useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import {
  ShieldCheck,
  ShieldAlert,
  ShieldX,
  BadgeCheck,
  Copy,
  Check,
  ArrowLeft,
  ArrowRight,
  AlertTriangle,
  Link2,
  Fingerprint,
  FileLock2,
  ExternalLink,
  Search,
  Wallet,
} from "lucide-react";

import AutheraLogo from "@/components/AutheraLogo";
import { toast } from "sonner";

import { api, formatCurrency, formatDate, truncHash } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import BenefitsSection from "@/components/BenefitsSection";

// ---------- shared small pieces ----------
// Polygon Amoy explorer helpers. Only used when a record has on-chain data
// (contract_address present). Seeded/mock receipts skip these links.
const POLYGON_AMOY_EXPLORER = "https://amoy.polygonscan.com";
const withHexPrefix = (h) =>
  h ? (String(h).startsWith("0x") ? String(h) : `0x${h}`) : "";
const explorerTxUrl = (h) =>
  h ? `${POLYGON_AMOY_EXPLORER}/tx/${withHexPrefix(h)}` : null;
const explorerTokenUrl = (contract, tokenId) =>
  contract && tokenId
    ? `${POLYGON_AMOY_EXPLORER}/token/${withHexPrefix(contract)}?a=${tokenId}`
    : null;
const explorerAddressUrl = (addr) =>
  addr ? `${POLYGON_AMOY_EXPLORER}/address/${withHexPrefix(addr)}` : null;

const CopyRow = ({ label, fullValue, displayValue, testid, explorerHref, explorerTestid }) => {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(fullValue || "");
      setCopied(true);
      toast.success("Copied");
      setTimeout(() => setCopied(false), 1200);
    } catch {
      toast.error("Copy failed");
    }
  };
  return (
    <div className="flex items-center justify-between gap-6 py-3.5 border-b border-gray-100 last:border-0">
      <span className="text-[10px] font-semibold tracking-[0.18em] uppercase text-gray-500">
        {label}
      </span>
      <div className="flex items-center gap-3">
        <button
          onClick={onCopy}
          data-testid={testid}
          className="inline-flex items-center gap-1.5 font-mono text-sm text-gray-900 hover:text-[#0055FF] transition group"
        >
          {displayValue}
          {copied ? (
            <Check size={13} className="text-green-600" />
          ) : (
            <Copy size={13} className="text-gray-400 group-hover:text-[#0055FF]" />
          )}
        </button>
        {explorerHref && (
          <a
            href={explorerHref}
            target="_blank"
            rel="noreferrer noopener"
            data-testid={explorerTestid}
            title="View on PolygonScan Amoy"
            className="inline-flex items-center text-gray-400 hover:text-[#0055FF] transition"
          >
            <ExternalLink size={13} />
          </a>
        )}
      </div>
    </div>
  );
};

const StaticRow = ({ label, value, testid }) => (
  <div className="flex items-center justify-between gap-6 py-3.5 border-b border-gray-100 last:border-0">
    <span className="text-[10px] font-semibold tracking-[0.18em] uppercase text-gray-500">
      {label}
    </span>
    <span data-testid={testid} className="text-sm text-gray-900 font-medium">
      {value}
    </span>
  </div>
);

const TrustItem = ({ icon: Icon, title, desc }) => (
  <div className="flex items-start gap-3">
    <span className="mt-0.5 h-8 w-8 shrink-0 rounded-lg bg-blue-50 text-[#0055FF] flex items-center justify-center">
      <Icon size={16} strokeWidth={2.2} />
    </span>
    <div>
      <div className="text-sm font-semibold text-gray-900">{title}</div>
      <div className="text-xs text-gray-500 mt-0.5 leading-relaxed">{desc}</div>
    </div>
  </div>
);

const BackLink = ({ nftId }) => {
  // Authenticated retailers should land back in the dashboard, not the
  // public verify lookup form. External visitors keep the original public
  // flow ("Verify another certificate" → /verify).
  const { user } = useAuth();
  const isRetailer = !!user;
  const to = isRetailer ? "/retailer" : "/verify";
  const label = isRetailer ? "Back to Retailer Dashboard" : "Verify another certificate";
  return (
    <div className="max-w-3xl mx-auto mb-6 flex items-center justify-between">
      <Link
        to={to}
        data-testid="verify-back"
        className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900"
      >
        <ArrowLeft size={14} />
        {label}
      </Link>
      <div className="font-mono text-xs text-gray-400">Certificate · {nftId}</div>
    </div>
  );
};

// ---------- status style tokens ----------
const STATUS_THEME = {
  verified: {
    accent: "bg-[#0055FF]",
    accentText: "text-[#0055FF]",
    icon: ShieldCheck,
    pillBg: "bg-[#0055FF] text-white",
    pillLabel: "VERIFIED",
    ring: "shadow-[0_10px_30px_rgba(0,85,255,0.45)]",
    topBar: "from-transparent via-[#0055FF] to-transparent",
    title: "Verified Ownership Certificate",
    subtext:
      "This asset is backed by a verifiable purchase record and cryptographic proof.",
  },
  unverified_ownership: {
    accent: "bg-amber-500",
    accentText: "text-amber-600",
    icon: ShieldAlert,
    pillBg: "bg-amber-500 text-white",
    pillLabel: "OWNERSHIP NOT CONFIRMED",
    ring: "shadow-[0_10px_30px_rgba(245,158,11,0.35)]",
    topBar: "from-transparent via-amber-400 to-transparent",
    title: "Ownership Not Verified",
    subtext:
      "This asset's digital ownership record has not been transferred to the current holder.",
  },
  mismatch: {
    accent: "bg-amber-500",
    accentText: "text-amber-600",
    icon: ShieldAlert,
    pillBg: "bg-amber-500 text-white",
    pillLabel: "MISMATCH DETECTED",
    ring: "shadow-[0_10px_30px_rgba(245,158,11,0.35)]",
    topBar: "from-transparent via-amber-400 to-transparent",
    title: "Verification Mismatch Detected",
    subtext:
      "This record exists, but key verification fields do not match the expected issuer-backed record.",
  },
};

// ---------- NOT FOUND state ----------
const NotFoundState = ({ nftId }) => (
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
    <BackLink nftId={nftId} />
    <div
      data-testid="verify-not-found"
      className="max-w-xl mx-auto bg-white border border-gray-200 rounded-2xl shadow-[0_12px_32px_rgba(0,0,0,0.05)] overflow-hidden"
    >
      <div className="h-[3px] bg-gradient-to-r from-transparent via-red-500 to-transparent" />
      <div className="p-10 text-center">
        <div className="mx-auto h-[68px] w-[68px] rounded-2xl bg-red-500 text-white flex items-center justify-center shadow-[0_10px_30px_rgba(239,68,68,0.35)]">
          <ShieldX size={30} strokeWidth={2.3} />
        </div>
        <div className="mt-6 inline-flex items-center gap-2 bg-red-500 text-white px-4 py-1.5 rounded-full text-xs font-bold tracking-wide shadow-[0_4px_14px_rgba(239,68,68,0.35)]">
          <AlertTriangle size={13} strokeWidth={2.5} />
          NOT VERIFIED
        </div>
        <h1 className="font-display font-extrabold tracking-tight text-gray-900 text-3xl mt-6">
          Certificate Not Verified
        </h1>
        <p className="mt-3 text-[15px] text-gray-700">
          This NFT is not recognized by the Authera verification system.
        </p>
        <p className="mt-2 text-sm text-gray-500 max-w-md mx-auto">
          The provided digital asset record could not be matched to a valid
          issuer-backed record.
        </p>
        <div className="mt-5 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 inline-block">
          <span className="text-[10px] font-semibold tracking-[0.18em] uppercase text-gray-500 mr-2">
            Submitted ID
          </span>
          <span className="font-mono text-sm text-gray-900">{nftId}</span>
        </div>
      </div>
    </div>
  </div>
);

const LoadingState = () => (
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
    <div className="max-w-xl mx-auto mt-16 py-16 text-center text-gray-500">
      Running verification checks…
    </div>
  </div>
);

// ---------- MAIN CERTIFICATE (verified / mismatch / unverified_ownership) ----------
const CertificateView = ({ result, celebrate, onResultUpdate }) => {
  const status = result.status;
  const theme = STATUS_THEME[status] || STATUS_THEME.verified;
  const StatusIcon = theme.icon;

  const r = result.receipt || {};
  const item = r.items?.[0] || {};
  const retailerInitials = (r.store_name || "R")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

  const currentUrl = typeof window !== "undefined" ? window.location.href : "";
  const lastVerifiedShort = new Date().toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).replace(",", " •");
  const verifiedTimestamp = new Date().toLocaleString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  return (
    <div
      data-testid={`verify-status-${status}`}
      className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10"
    >
      <BackLink nftId={result.verification_id || r.nft_id || result.nft_id} />

      <div className="relative max-w-3xl mx-auto rise-in">
        <div
          className={`relative bg-white rounded-[28px] border border-gray-200 shadow-[0_32px_80px_-20px_rgba(0,0,0,0.12)] overflow-hidden ${
            celebrate && status === "verified" ? "celebrate-pulse" : ""
          }`}
        >
          <div className="absolute inset-0 pointer-events-none rounded-[28px] ring-1 ring-inset ring-white/60" />
          <div className="relative m-[10px] rounded-[20px] border border-gray-200/80 bg-gradient-to-b from-white via-white to-gray-50/40 overflow-hidden">
            <div className={`h-[3px] bg-gradient-to-r ${theme.topBar}`} />

            {/* Warning banner for non-verified states */}
            {status !== "verified" && (
              <div
                data-testid="status-banner"
                className="mx-8 sm:mx-12 mt-6 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex items-start gap-3"
              >
                <AlertTriangle
                  size={18}
                  className="text-amber-600 shrink-0 mt-0.5"
                />
                <div className="text-sm text-amber-900 leading-relaxed">
                  {status === "mismatch"
                    ? "Key verification fields do not match the issuer-backed reference for this asset. Review details carefully before any transaction."
                    : "Ownership transfer has not been completed. The current holder is not yet recognized as the verified owner."}
                </div>
              </div>
            )}

            {/* Hero seal — Authera logo above the VERIFIED pill (per brand spec) */}
            <div className="px-8 sm:px-12 pt-10 pb-10 text-center">
              <div className="flex justify-center">
                <AutheraLogo size={64} glow testid="verify-brand-logo" />
              </div>

              <div
                data-testid="verify-badge"
                className={`mt-5 inline-flex items-center gap-2 ${theme.pillBg} px-4 py-1.5 rounded-full text-xs font-bold tracking-wide`}
              >
                <BadgeCheck size={14} strokeWidth={2.5} />
                {theme.pillLabel}
              </div>

              <h1 className="font-display font-extrabold tracking-tight text-gray-900 text-3xl sm:text-4xl mt-6 leading-[1.1]">
                {theme.title}
              </h1>
              <p className="mt-4 text-[15px] text-gray-600 max-w-lg mx-auto leading-relaxed">
                {theme.subtext}
              </p>
              <div
                data-testid="last-verified"
                className="mt-4 inline-flex items-center gap-1.5 text-[11px] font-mono text-gray-500"
              >
                <span className="h-1 w-1 rounded-full bg-gray-400" />
                Last verified: {lastVerifiedShort}
              </div>
            </div>

            {/* Issued By */}
            <div className="px-8 sm:px-12 pb-8">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-px flex-1 bg-gray-200" />
                <span className="text-[10px] font-semibold tracking-[0.3em] uppercase text-gray-400">
                  Issued By
                </span>
                <div className="h-px flex-1 bg-gray-200" />
              </div>
              <div
                data-testid="issued-by"
                className="bg-white border border-gray-200 rounded-xl px-5 py-4 flex items-center gap-4"
              >
                <div className="shrink-0 h-12 w-12 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 border border-gray-200 flex items-center justify-center">
                  <span className="font-display font-bold text-gray-700 text-sm tracking-wider">
                    {retailerInitials}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-gray-900 truncate">
                      {r.store_name}
                    </span>
                    <span className="inline-flex items-center gap-1 bg-blue-50 text-[#0055FF] border border-blue-200 px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wide">
                      <BadgeCheck size={11} strokeWidth={2.5} />
                      Verified Retailer
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                    {r.retailer_location && (
                      <>
                        <span>{r.retailer_location}</span>
                        <span className="text-gray-300">·</span>
                      </>
                    )}
                    <span className="font-medium text-gray-600">
                      Authorized Retailer
                    </span>
                  </div>
                </div>
              </div>
              <p className="mt-3 text-xs text-gray-500 text-center">
                Issued by an authorized retailer at the time of purchase.
                <span className="hidden sm:inline text-gray-300 mx-2">·</span>
                <span className="block sm:inline mt-0.5 sm:mt-0 text-gray-400">
                  Retailer identity verified at issuance.
                </span>
              </p>
            </div>

            {/* Product hero — premium navy gradient with the actual purchased item */}
            {r.product_image_url && (
              <div className="px-8 sm:px-12 pb-6">
                <div
                  data-testid="verify-product-hero"
                  className="relative aspect-[16/9] overflow-hidden rounded-2xl border border-[#0a1628]/15 bg-gradient-to-br from-[#0a1628] via-[#0f1f3a] to-[#0a1628] shadow-[0_18px_48px_rgba(10,22,40,0.18)]"
                >
                  <img
                    src={r.product_image_url}
                    alt={item.name || r.store_name}
                    className="absolute inset-0 w-full h-full object-contain p-8 drop-shadow-[0_12px_32px_rgba(0,0,0,0.55)]"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />
                  <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 bg-white/95 backdrop-blur-sm border border-white/40 text-[10px] font-bold tracking-wider uppercase text-[#0055FF] px-2.5 py-1 rounded-md">
                    <BadgeCheck size={11} strokeWidth={2.5} />
                    Verified
                  </div>
                  <div className="absolute top-4 right-4 z-10">
                    <div className="flex items-center justify-center w-12 h-12 rounded-2xl bg-blue-600 shadow-[0_0_22px_rgba(37,99,235,0.75),0_10px_22px_rgba(0,0,0,0.4)]">
                      <AutheraLogo size={36} glow />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Asset */}
            <div className="px-8 sm:px-12">
              <div className="flex items-center gap-4">
                <div className="h-px flex-1 bg-gray-200" />
                <span className="text-[10px] font-semibold tracking-[0.3em] uppercase text-gray-400">
                  Asset
                </span>
                <div className="h-px flex-1 bg-gray-200" />
              </div>
            </div>

            <div className="px-8 sm:px-12 py-10 text-center">
              <div
                data-testid="verify-item"
                className="font-display font-extrabold text-gray-900 text-3xl sm:text-5xl tracking-[-0.015em] leading-[1.05]"
              >
                {item.name || result.model || "Item"}
              </div>
              {r.store_name && (
                <div
                  data-testid="verify-issued-by"
                  className="mt-3 text-sm sm:text-base text-gray-500 font-normal"
                >
                  Issued by{" "}
                  <span className="text-gray-700 font-medium">{r.store_name}</span>
                </div>
              )}
              <div
                data-testid="verify-hero-trust-line"
                className="mt-1.5 text-[11px] tracking-wide text-gray-400 font-normal"
              >
                Verified by Authera
              </div>

              {Number.isFinite(r.total) && r.total > 0 && (
                <div
                  data-testid="verify-total"
                  className={`mt-8 font-display font-bold text-5xl sm:text-6xl tracking-tight ${
                    status === "verified" ? "text-[#0055FF]" : "text-gray-900"
                  }`}
                >
                  {formatCurrency(r.total, r.currency)}
                </div>
              )}
              <div
                data-testid="verify-date-line"
                className={`${Number.isFinite(r.total) && r.total > 0 ? "mt-2" : "mt-6"} text-sm text-gray-500`}
              >
                {Number.isFinite(r.total) && r.total > 0 ? "Purchased on" : "Issued on"}{" "}
                <span className="text-gray-900 font-medium">
                  {formatDate(r.purchase_date)}
                </span>
              </div>
            </div>

            {/* Mismatch fields list */}
            {status === "mismatch" && (result.mismatch_fields || []).length > 0 && (
              <div className="px-8 sm:px-12 pb-6 space-y-4">
                <div
                  data-testid="mismatch-fields"
                  className="bg-amber-50/60 border border-amber-200 rounded-xl p-5"
                >
                  <div className="flex items-center gap-2 text-amber-900 font-semibold text-sm">
                    <AlertTriangle size={15} />
                    Fields that do not match
                  </div>
                  <ul className="mt-3 space-y-1.5">
                    {result.mismatch_fields.map((f) => (
                      <li
                        key={f}
                        className="text-sm text-amber-900 flex items-center gap-2 capitalize"
                      >
                        <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                        {f.replace(/_/g, " ")}
                      </li>
                    ))}
                  </ul>
                </div>

                {(result.mismatch_details || []).length > 0 && (
                  <div
                    data-testid="verification-details"
                    className="bg-white border border-gray-200 rounded-xl p-5"
                  >
                    <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-gray-700">
                      Verification Details
                    </div>
                    <div className="mt-3 space-y-3">
                      {result.mismatch_details.map((m) => (
                        <div
                          key={m.field}
                          className="rounded-lg border border-gray-200 overflow-hidden"
                        >
                          <div className="px-4 py-2 bg-gray-50 text-xs font-semibold text-gray-700 capitalize border-b border-gray-200">
                            {m.field.replace(/_/g, " ")}
                          </div>
                          <div className="divide-y divide-gray-100">
                            <div className="flex items-start gap-4 px-4 py-2.5">
                              <span className="text-[10px] font-semibold tracking-[0.18em] uppercase text-gray-500 w-20 shrink-0 mt-0.5">
                                Expected
                              </span>
                              <span className="text-sm text-gray-900 font-medium break-words">
                                {m.expected || "—"}
                              </span>
                            </div>
                            <div className="flex items-start gap-4 px-4 py-2.5 bg-amber-50/40">
                              <span className="text-[10px] font-semibold tracking-[0.18em] uppercase text-amber-700 w-20 shrink-0 mt-0.5">
                                Actual
                              </span>
                              <span className="text-sm text-amber-900 font-medium break-words">
                                {m.actual || "—"}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Record + QR */}
            <div className="px-8 sm:px-12">
              <div className="flex items-center gap-4">
                <div className="h-px flex-1 bg-gray-200" />
                <span className="text-[10px] font-semibold tracking-[0.3em] uppercase text-gray-400">
                  Record
                </span>
                <div className="h-px flex-1 bg-gray-200" />
              </div>
            </div>

            <div className="px-8 sm:px-12 py-8 grid md:grid-cols-5 gap-8 md:gap-10 items-start">
              <div className="md:col-span-3">
                {r.tx_hash && (
                  <div className="flex items-center justify-between gap-6 py-3.5 border-b border-gray-100">
                    <span className="text-[10px] font-semibold tracking-[0.18em] uppercase text-gray-500">
                      Network
                    </span>
                    <span
                      data-testid="verify-network"
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-900"
                    >
                      <span className="h-1.5 w-1.5 rounded-full bg-violet-500" />
                      Polygon Amoy
                    </span>
                  </div>
                )}
                <CopyRow
                  label="Certificate ID"
                  fullValue={result.verification_id || r.nft_id}
                  displayValue={result.verification_id || r.nft_id}
                  testid="verify-cert-id"
                />
                <CopyRow
                  label="Verification Transaction"
                  fullValue={r.tx_hash ? withHexPrefix(r.tx_hash) : ""}
                  displayValue={truncHash(withHexPrefix(r.tx_hash), 10, 6)}
                  testid="verify-tx"
                  explorerHref={r.tx_hash ? explorerTxUrl(r.tx_hash) : null}
                  explorerTestid="verify-tx-explorer"
                />
                <StaticRow
                  label="Issued"
                  value={formatDate(result.issue_date || r.updated_at)}
                />
                {item.sku && (
                  <div className="flex items-center justify-between gap-6 py-3 border-b border-gray-100 last:border-0">
                    <span className="text-[10px] font-semibold tracking-[0.18em] uppercase text-gray-500">
                      SKU
                    </span>
                    <span
                      data-testid="verify-item-sku"
                      className="text-xs font-mono text-gray-500"
                    >
                      {item.sku}
                    </span>
                  </div>
                )}
              </div>

              <div className="md:col-span-2 flex flex-col items-center">
                <div className="relative bg-white p-3 rounded-2xl border border-gray-200 shadow-[0_4px_16px_rgba(0,0,0,0.06)]">
                  <div className="absolute inset-0 rounded-2xl ring-1 ring-inset ring-[#0055FF]/10 pointer-events-none" />
                  <QRCodeSVG
                    value={currentUrl || `https://verify/${r.nft_id}`}
                    size={148}
                    bgColor="#FFFFFF"
                    fgColor={status === "verified" ? "#0055FF" : "#111827"}
                    level="M"
                  />
                </div>
                <div className="mt-4 text-xs font-semibold tracking-[0.15em] uppercase text-gray-700 text-center">
                  Scan to verify authenticity
                </div>
                <div className="mt-1.5 text-[11px] text-gray-500 text-center max-w-[180px] leading-snug">
                  Anyone can scan this code to confirm the record.
                </div>
              </div>
            </div>

            {/* Benefits & Access */}
            <BenefitsSection
              status={status}
              receipt={r}
              onReceiptUpdate={(updated) =>
                onResultUpdate?.({ ...result, receipt: updated })
              }
            />

            {/* Trust indicators */}
            <div className="px-8 sm:px-12 pb-10">
              <div className="bg-white border border-gray-200 rounded-xl p-6 grid sm:grid-cols-3 gap-6">
                <TrustItem
                  icon={Link2}
                  title="Verified on-chain"
                  desc="Recorded on a ledger with public references."
                />
                <TrustItem
                  icon={FileLock2}
                  title="Immutable record"
                  desc="Entries cannot be edited after issuance."
                />
                <TrustItem
                  icon={Fingerprint}
                  title="Tamper-resistant metadata"
                  desc="Cryptographic hash detects any alteration."
                />
              </div>
            </div>

            {/* Authority footer */}
            <div className="px-8 sm:px-12 py-6 border-t border-gray-200 bg-gray-50/70 text-center">
              <div
                data-testid="verify-trust-line"
                className="text-[12px] text-gray-600 max-w-md mx-auto leading-relaxed mb-4"
              >
                This certificate is anchored on-chain and corresponds to an
                official purchase record issued by{" "}
                <span className="font-semibold text-gray-900">
                  {r.store_name?.trim() || "the retailer"}
                </span>
                .
              </div>
              <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-gray-700">
                Verified by Authera
              </div>
              <div className="text-[11px] text-gray-500 mt-1">
                Digital Asset Verification Layer
              </div>
              <div className="mt-3 text-xs text-gray-500">
                Verification timestamp:{" "}
                <span className="text-gray-900 font-medium">
                  {verifiedTimestamp}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="absolute -inset-4 -z-10 bg-gradient-to-b from-blue-50/40 via-transparent to-transparent blur-2xl rounded-full pointer-events-none" />
      </div>
    </div>
  );
};

// ---------- Public lookup form (when no identifier in URL) ----------
const TX_HASH_RE = /^(0x)?[a-fA-F0-9]{64}$/;

function VerifyLookupForm() {
  const [value, setValue] = useState("");
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const onSubmit = (e) => {
    e.preventDefault();
    const trimmed = (value || "").trim();
    if (!trimmed) {
      setError("Enter a Certificate ID or Verification Transaction hash.");
      return;
    }
    setError("");
    navigate(`/verify/${encodeURIComponent(trimmed)}`);
  };

  return (
    <div
      data-testid="verify-lookup"
      className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16"
    >
      <div className="max-w-xl mx-auto">
        <div className="text-center">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-[#0055FF] border border-blue-200 text-xs font-semibold tracking-wide">
            <ShieldCheck size={13} strokeWidth={2.5} />
            Public Verification Portal
          </span>
          <h1 className="font-display font-extrabold tracking-tight text-gray-900 text-4xl sm:text-5xl mt-5">
            Verify a Certificate
          </h1>
          <p className="mt-4 text-[15px] text-gray-600 leading-relaxed">
            Confirm the authenticity of any Authera-issued digital certificate.
            Paste the Certificate ID or Verification Transaction hash printed on
            your record.
          </p>
        </div>

        <form
          onSubmit={onSubmit}
          className="mt-10 bg-white border border-gray-200 rounded-2xl shadow-[0_12px_32px_rgba(0,0,0,0.05)] overflow-hidden"
        >
          <div className="h-[3px] bg-gradient-to-r from-transparent via-[#0055FF] to-transparent" />
          <div className="p-6 sm:p-8">
            <label
              htmlFor="verify-input"
              className="text-[10px] font-semibold tracking-[0.18em] uppercase text-gray-500"
            >
              Certificate ID or Verification Transaction
            </label>
            <div className="mt-2 flex items-stretch gap-2">
              <input
                id="verify-input"
                data-testid="verify-input"
                type="text"
                autoFocus
                placeholder="NFT-XXXXXXXXXX or 0x…"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                className="flex-1 rounded-md border border-gray-300 px-3 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#0055FF]/30 focus:border-[#0055FF]"
              />
              <button
                type="submit"
                data-testid="verify-submit"
                className="inline-flex items-center justify-center gap-1.5 bg-[#0055FF] text-white px-4 py-2.5 rounded-md text-sm font-semibold hover:bg-[#0044CC] transition shadow-sm"
              >
                <Search size={14} />
                Verify
                <ArrowRight size={14} />
              </button>
            </div>
            {error && (
              <div data-testid="verify-input-error" className="mt-3 text-xs text-red-600 font-medium">
                {error}
              </div>
            )}
            <div className="mt-4 text-[11px] text-gray-500 leading-relaxed">
              We never reveal personal information. Verification only confirms
              the certificate's authenticity, retailer, and issued date.
            </div>
          </div>
        </form>

        <div className="mt-10 grid sm:grid-cols-3 gap-6">
          <TrustItem
            icon={Link2}
            title="On-chain record"
            desc="Certificates are anchored on a public ledger."
          />
          <TrustItem
            icon={FileLock2}
            title="Privacy-safe"
            desc="No customer email, address, or full serial shown."
          />
          <TrustItem
            icon={Fingerprint}
            title="Tamper-evident"
            desc="Any change is detectable through the cryptographic record."
          />
        </div>
      </div>
    </div>
  );
}

// ---------- Tx-hash result (from /api/public/verify/{tx_hash}) ----------
function TxVerifyView({ data, identifier }) {
  const { user } = useAuth();
  const isRetailer = !!user;
  const tryAgainHref = isRetailer ? "/retailer" : "/verify";
  const tryAgainLabel = isRetailer ? "Back to Retailer Dashboard" : "Try another certificate";
  const verified = !!data?.verified;
  const StatusIcon = verified ? ShieldCheck : ShieldX;
  const accent = verified ? "bg-[#0055FF]" : "bg-red-500";
  const ring = verified
    ? "shadow-[0_10px_30px_rgba(0,85,255,0.45)]"
    : "shadow-[0_10px_30px_rgba(239,68,68,0.35)]";
  const pillBg = verified ? "bg-[#0055FF] text-white" : "bg-red-500 text-white";
  const pillLabel = verified ? "VERIFIED" : "NOT VERIFIED";
  const topBar = verified
    ? "from-transparent via-[#0055FF] to-transparent"
    : "from-transparent via-red-500 to-transparent";

  const currentUrl = typeof window !== "undefined" ? window.location.href : "";
  const verifiedTimestamp = new Date().toLocaleString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  return (
    <div
      data-testid={`verify-tx-status-${verified ? "verified" : "not_verified"}`}
      className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10"
    >
      <BackLink nftId={data?.certificate_id || identifier} />
      <div className="relative max-w-3xl mx-auto rise-in">
        <div className="relative bg-white rounded-[28px] border border-gray-200 shadow-[0_32px_80px_-20px_rgba(0,0,0,0.12)] overflow-hidden">
          <div className="relative m-[10px] rounded-[20px] border border-gray-200/80 bg-gradient-to-b from-white via-white to-gray-50/40 overflow-hidden">
            <div className={`h-[3px] bg-gradient-to-r ${topBar}`} />

            {!verified && (
              <div
                data-testid="tx-not-verified-banner"
                className="mx-8 sm:mx-12 mt-6 bg-red-50 border border-red-200 rounded-lg px-4 py-3 flex items-start gap-3"
              >
                <AlertTriangle size={18} className="text-red-600 shrink-0 mt-0.5" />
                <div className="text-sm text-red-900 leading-relaxed">
                  {data?.reason ||
                    "No certificate could be matched to this transaction hash."}
                </div>
              </div>
            )}

            <div className="px-8 sm:px-12 pt-10 pb-8 text-center">
              {verified ? (
                <div className="flex justify-center">
                  <AutheraLogo size={64} glow testid="verify-tx-brand-logo" />
                </div>
              ) : (
                <div className="relative mx-auto h-[72px] w-[72px]">
                  <div className={`absolute inset-0 rounded-2xl ${accent} ${ring}`} />
                  <div className="absolute inset-0 rounded-2xl ring-1 ring-inset ring-white/30" />
                  <div className="relative h-full w-full flex items-center justify-center text-white">
                    <StatusIcon size={32} strokeWidth={2.3} />
                  </div>
                </div>
              )}
              <div
                data-testid="verify-badge"
                className={`mt-${verified ? 5 : 6} inline-flex items-center gap-2 ${pillBg} px-4 py-1.5 rounded-full text-xs font-bold tracking-wide`}
              >
                <BadgeCheck size={14} strokeWidth={2.5} />
                {pillLabel}
              </div>
              <h1 className="font-display font-extrabold tracking-tight text-gray-900 text-3xl sm:text-4xl mt-6 leading-[1.1]">
                {verified
                  ? "Verified Digital Certificate"
                  : "Certificate Not Verified"}
              </h1>
              <p className="mt-4 text-[15px] text-gray-600 max-w-lg mx-auto leading-relaxed">
                {verified
                  ? "This certificate is anchored on-chain and matches an Authera-issued record."
                  : "We could not match this transaction hash to any issued certificate."}
              </p>
            </div>

            {verified && (
              <>
                {data.product_image_url && (
                  <div className="px-8 sm:px-12 pb-2">
                    <div
                      data-testid="tx-product-hero"
                      className="relative aspect-[16/9] overflow-hidden rounded-2xl border border-[#0a1628]/15 bg-gradient-to-br from-[#0a1628] via-[#0f1f3a] to-[#0a1628] shadow-[0_18px_48px_rgba(10,22,40,0.18)]"
                    >
                      <img
                        src={data.product_image_url}
                        alt={data.product_name}
                        className="absolute inset-0 w-full h-full object-contain p-8 drop-shadow-[0_12px_32px_rgba(0,0,0,0.55)]"
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />
                      <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 bg-white/95 backdrop-blur-sm border border-white/40 text-[10px] font-bold tracking-wider uppercase text-[#0055FF] px-2.5 py-1 rounded-md">
                        <BadgeCheck size={11} strokeWidth={2.5} />
                        Verified
                      </div>
                      <div className="absolute top-4 right-4 z-10">
                        <div className="flex items-center justify-center w-12 h-12 rounded-2xl bg-blue-600 shadow-[0_0_22px_rgba(37,99,235,0.75),0_10px_22px_rgba(0,0,0,0.4)]">
                          <AutheraLogo size={36} glow />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                <div className="px-8 sm:px-12">
                  <div className="flex items-center gap-4">
                    <div className="h-px flex-1 bg-gray-200" />
                    <span className="text-[10px] font-semibold tracking-[0.3em] uppercase text-gray-400">
                      Asset
                    </span>
                    <div className="h-px flex-1 bg-gray-200" />
                  </div>
                </div>
                <div className="px-8 sm:px-12 py-8 text-center">
                  <div
                    data-testid="tx-product-name"
                    className="font-display font-extrabold text-gray-900 text-2xl sm:text-3xl tracking-[0.02em]"
                  >
                    {data.product_name}
                  </div>
                  <div
                    data-testid="tx-retailer"
                    className="mt-2 text-sm text-gray-600"
                  >
                    Issued by{" "}
                    <span className="font-semibold text-gray-900">
                      {data.retailer}
                    </span>
                  </div>
                  {data.serial_masked && (
                    <div
                      data-testid="tx-serial-masked"
                      className="mt-3 inline-flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-full px-3 py-1 font-mono text-xs text-gray-700"
                    >
                      Serial {data.serial_masked}
                    </div>
                  )}
                </div>

                <div className="px-8 sm:px-12">
                  <div className="flex items-center gap-4">
                    <div className="h-px flex-1 bg-gray-200" />
                    <span className="text-[10px] font-semibold tracking-[0.3em] uppercase text-gray-400">
                      Record
                    </span>
                    <div className="h-px flex-1 bg-gray-200" />
                  </div>
                </div>
                <div className="px-8 sm:px-12 py-8 grid md:grid-cols-5 gap-8 md:gap-10 items-start">
                  <div className="md:col-span-3">
                    {data.network && (
                      <div className="flex items-center justify-between gap-6 py-3.5 border-b border-gray-100">
                        <span className="text-[10px] font-semibold tracking-[0.18em] uppercase text-gray-500">
                          Network
                        </span>
                        <span
                          data-testid="verify-network"
                          className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-900"
                        >
                          <span className="h-1.5 w-1.5 rounded-full bg-violet-500" />
                          {data.network}
                        </span>
                      </div>
                    )}
                    {data.certificate_id && (
                      <CopyRow
                        label="Certificate ID"
                        fullValue={String(data.certificate_id)}
                        displayValue={String(data.certificate_id)}
                        testid="verify-certificate-id"
                      />
                    )}
                    <CopyRow
                      label="Verification Transaction"
                      fullValue={withHexPrefix(data.tx_hash)}
                      displayValue={truncHash(withHexPrefix(data.tx_hash), 10, 6)}
                      testid="verify-tx"
                      explorerHref={data.tx_hash ? explorerTxUrl(data.tx_hash) : null}
                      explorerTestid="verify-tx-explorer"
                    />
                    {data.minted_at && (
                      <StaticRow
                        label="Issued"
                        value={formatDate(data.minted_at)}
                        testid="verify-issued"
                      />
                    )}
                  </div>

                  <div className="md:col-span-2 flex flex-col items-center">
                    <div className="relative bg-white p-3 rounded-2xl border border-gray-200 shadow-[0_4px_16px_rgba(0,0,0,0.06)]">
                      <QRCodeSVG
                        value={currentUrl || `https://verify/${data.tx_hash}`}
                        size={148}
                        bgColor="#FFFFFF"
                        fgColor={"#0055FF"}
                        level="M"
                      />
                    </div>
                    <div className="mt-4 text-xs font-semibold tracking-[0.15em] uppercase text-gray-700 text-center">
                      Scan to verify authenticity
                    </div>
                  </div>
                </div>

                <div className="px-8 sm:px-12 pb-10">
                  <div className="bg-white border border-gray-200 rounded-xl p-6 grid sm:grid-cols-3 gap-6">
                    <TrustItem
                      icon={Link2}
                      title="Verified on-chain"
                      desc="Recorded on a ledger with public references."
                    />
                    <TrustItem
                      icon={FileLock2}
                      title="Privacy-safe"
                      desc="No customer email, wallet, or full serial shown."
                    />
                    <TrustItem
                      icon={Fingerprint}
                      title="Tamper-resistant"
                      desc="Cryptographic hash detects any alteration."
                    />
                  </div>
                </div>

                {data.claim_available && (
                  <div className="px-8 sm:px-12 pb-10">
                    <div
                      data-testid="verify-claim-cta"
                      className="bg-gray-900 text-white rounded-xl p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4"
                    >
                      <div className="flex-1">
                        <div className="text-[10px] font-semibold tracking-[0.18em] uppercase text-blue-200">
                          Custodial certificate
                        </div>
                        <div className="mt-1 text-sm leading-relaxed text-gray-100">
                          Your certificate is currently secured in an Authera Wallet.
                          Connect your personal wallet to take full ownership.
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

            {!verified && (
              <div className="px-8 sm:px-12 pb-10 text-center">
                <Link
                  to={tryAgainHref}
                  data-testid="verify-try-again"
                  className="inline-flex items-center gap-2 bg-white text-gray-900 border border-gray-200 px-4 py-2 rounded-md text-sm font-semibold hover:border-[#0055FF] hover:text-[#0055FF] transition"
                >
                  <Search size={14} />
                  {tryAgainLabel}
                </Link>
              </div>
            )}

            <div className="px-8 sm:px-12 py-6 border-t border-gray-200 bg-gray-50/70 text-center">
              {verified && (
                <div
                  data-testid="tx-trust-line"
                  className="text-[12px] text-gray-600 max-w-md mx-auto leading-relaxed mb-4"
                >
                  This certificate is anchored on-chain and corresponds to an
                  official purchase record issued by{" "}
                  <span className="font-semibold text-gray-900">
                    {data?.retailer && String(data.retailer).trim() && data.retailer !== "—"
                      ? data.retailer
                      : "the retailer"}
                  </span>
                  .
                </div>
              )}
              <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-gray-700">
                Verified by Authera
              </div>
              <div className="text-[11px] text-gray-500 mt-1">
                Digital Asset Verification Layer
              </div>
              <div className="mt-3 text-xs text-gray-500">
                Verification timestamp:{" "}
                <span className="text-gray-900 font-medium">
                  {verifiedTimestamp}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- top-level page ----------
export default function Verify() {
  const { nftId } = useParams();
  const location = useLocation();
  const [result, setResult] = useState(null);
  const [txResult, setTxResult] = useState(null);
  const [loading, setLoading] = useState(false);

  // celebrate flag: from navigation state (after simulate transfer / verification page)
  const [celebrate, setCelebrate] = useState(false);
  useEffect(() => {
    if (location.state?.celebrate) {
      setCelebrate(true);
      const t = setTimeout(() => setCelebrate(false), 2000);
      return () => clearTimeout(t);
    }
  }, [location.state]);

  const isTxHash = nftId ? TX_HASH_RE.test(nftId.trim()) : false;

  useEffect(() => {
    if (!nftId) {
      setResult(null);
      setTxResult(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setResult(null);
      setTxResult(null);
      try {
        if (isTxHash) {
          const cleaned = nftId.trim().toLowerCase().replace(/^0x/, "");
          const { data } = await api.get(`/public/verify/${cleaned}`);
          if (!cancelled) setTxResult(data);
        } else {
          const { data } = await api.get(`/verify/${nftId}`);
          if (!cancelled) setResult(data);
        }
      } catch {
        if (!cancelled) {
          if (isTxHash) {
            setTxResult({ verified: false, reason: "Lookup failed." });
          } else {
            setResult({ status: "not_found", nft_id: nftId });
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [nftId, isTxHash]);

  // No identifier in URL → public lookup form
  if (!nftId) return <VerifyLookupForm />;

  if (loading) return <LoadingState />;

  // tx-hash route
  if (isTxHash) {
    if (!txResult) return <LoadingState />;
    return <TxVerifyView data={txResult} identifier={nftId} />;
  }

  // nft_id route
  if (!result || result.status === "not_found") return <NotFoundState nftId={nftId} />;
  return (
    <CertificateView
      result={result}
      celebrate={celebrate}
      onResultUpdate={setResult}
    />
  );
}
